import { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';

interface GameFormData {
  title: string;
  description: string;
  url: string;
  imageUrl: string;
  isActive: boolean;
  requiredTokens: number;
  category: string;
}

interface GameConfigFormProps {
  initialData?: GameFormData;
  onSubmit: (data: GameFormData) => void;
  onCancel: () => void;
  isEditing?: boolean;
}

interface FormErrors {
  title?: string;
  description?: string;
  url?: string;
  imageUrl?: string;
  requiredTokens?: string;
}

const CATEGORIES = [
  { id: 'action', label: 'Action' },
  { id: 'puzzle', label: 'Puzzle' },
  { id: 'strategy', label: 'Strategy' },
  { id: 'arcade', label: 'Arcade' },
  { id: 'simulation', label: 'Simulation' },
  { id: 'rpg', label: 'RPG' }
];

const GameConfigForm = ({ initialData, onSubmit, onCancel, isEditing }: GameConfigFormProps) => {
  const [formData, setFormData] = useState<GameFormData>({
    title: '',
    description: '',
    url: '',
    imageUrl: '',
    isActive: true,
    requiredTokens: 0,
    category: 'action'
  });
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.url.trim()) {
      newErrors.url = 'URL is required';
    } else if (!formData.url.startsWith('/games/')) {
      newErrors.url = 'URL must start with /games/';
    }

    if (!formData.imageUrl.trim()) {
      newErrors.imageUrl = 'Image URL is required';
    }

    if (formData.requiredTokens < 0) {
      newErrors.requiredTokens = 'Required tokens cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-slate-700 p-6 rounded-lg mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Title <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => {
              setFormData({...formData, title: e.target.value});
              setErrors({...errors, title: undefined});
            }}
            className={`w-full bg-slate-600 rounded px-3 py-2 ${
              errors.title ? 'border border-red-400' : ''
            }`}
            placeholder="Enter game title"
          />
          {errors.title && (
            <p className="text-red-400 text-sm mt-1">{errors.title}</p>
          )}
        </div>

        {/* URL */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Game URL <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={formData.url}
            onChange={(e) => {
              setFormData({...formData, url: e.target.value});
              setErrors({...errors, url: undefined});
            }}
            className={`w-full bg-slate-600 rounded px-3 py-2 ${
              errors.url ? 'border border-red-400' : ''
            }`}
            placeholder="/games/your-game-name"
          />
          {errors.url && (
            <p className="text-red-400 text-sm mt-1">{errors.url}</p>
          )}
        </div>

        {/* Game Image */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Game Image <span className="text-red-400">*</span>
          </label>
          <ImageUpload
            onImageUploaded={(url) => {
              setFormData({...formData, imageUrl: url});
              setErrors({...errors, imageUrl: undefined});
            }}
            currentImageUrl={formData.imageUrl}
          />
        </div>

        {/* Required Tokens */}
        <div>
          <label className="block text-sm font-medium mb-1">Required Tokens</label>
          <input
            type="number"
            value={formData.requiredTokens}
            onChange={(e) => {
              setFormData({...formData, requiredTokens: Number(e.target.value)});
              setErrors({...errors, requiredTokens: undefined});
            }}
            className={`w-full bg-slate-600 rounded px-3 py-2 ${
              errors.requiredTokens ? 'border border-red-400' : ''
            }`}
            min="0"
          />
          {errors.requiredTokens && (
            <p className="text-red-400 text-sm mt-1">{errors.requiredTokens}</p>
          )}
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">
            Description <span className="text-red-400">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => {
              setFormData({...formData, description: e.target.value});
              setErrors({...errors, description: undefined});
            }}
            className={`w-full bg-slate-600 rounded px-3 py-2 ${
              errors.description ? 'border border-red-400' : ''
            }`}
            rows={3}
            placeholder="Enter game description"
          />
          {errors.description && (
            <p className="text-red-400 text-sm mt-1">{errors.description}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium mb-1">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
            className="w-full bg-slate-600 rounded px-3 py-2"
          >
            {CATEGORIES.map(category => (
              <option key={category.id} value={category.id}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        {/* Active Status */}
        <div className="flex items-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
              className="mr-2 rounded"
            />
            Active
          </label>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex gap-2 mt-6">
        <button 
          type="submit" 
          className="bg-green-500 px-4 py-2 rounded hover:bg-green-600 transition-colors"
        >
          {isEditing ? 'Update Game' : 'Add Game'}
        </button>
        <button 
          type="button" 
          onClick={onCancel}
          className="bg-gray-500 px-4 py-2 rounded hover:bg-gray-600 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default GameConfigForm; 